// Trades & Business Units. A tenant adds the trades their company offers from
// the provisioner catalog (picking a color + icon), and manages business units
// with or without a trade assignment. Trades' color/icon are editable later,
// and any trade can be removed. Business Units were a standalone page —
// /business-units now redirects here. Archived business units stay recoverable.
import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  Building2,
  MoreHorizontal,
  Palette,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { useTaxPolicies } from '@/hooks/taxes/useTaxPolicies';
import { useServiceAreas } from '@/hooks/service-areas/useServiceAreas';
import { ServiceAreaZipPicker } from '@/components/service-areas/ServiceAreaZipPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { TradeIcon } from '@/components/scheduler/TradeIcon';
import { TRADE_ICON_NAMES } from '@/components/scheduler/trade-icons';
import {
  TRADE_COLOR_KEYS,
  TRADE_DOT_CLASS,
  tradeBadgeClass,
  tradeColorKey,
} from '@/lib/scheduler/trade-colors';
import { NOTIFI_PASTEL_COLORS } from '@/lib/design/brand-colors';
import { useTradeCatalog, useEnableTrade } from '@/hooks/scheduler/useTradeCatalog';
import { useTrades, useUpdateTrade } from '@/hooks/scheduler/useTrades';
import {
  useArchiveBusinessUnit,
  useBusinessUnitArchiveImpact,
  useBusinessUnits,
  useCreateBusinessUnit,
  useUnarchiveBusinessUnit,
  useUpdateBusinessUnit,
} from '@/hooks/scheduler/useBusinessUnits';
import { cn } from '@/lib/utils';
import { isApiError } from '@/lib/api/errors';
import {
  BUSINESS_UNIT_SEGMENT_HINTS,
  BUSINESS_UNIT_SEGMENT_LABELS,
  ARCHIVE_IMPACT_CHANGED,
  type BusinessUnit,
  type BusinessUnitArchiveImpact,
  type BusinessUnitArchiveSection,
  type BusinessUnitSegment,
} from '@/types/business-unit';
import type { Trade, TradeColor } from '@/types/scheduler';

export const Route = createFileRoute('/_dashboard/dashboard/settings/trades')({
  component: TradesPage,
});

const BU_DEFAULT_COLOR = '#888888';

/** Default BU code derived from the name (uppercased slug). Mirrors the backend
 *  auto-derivation, e.g. "HVAC — North" → "HVAC-NORTH". */
function deriveBuCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const BU_PRESET_COLORS = [...NOTIFI_PASTEL_COLORS];

interface BuGroups {
  active: BusinessUnit[];
  archived: BusinessUnit[];
}

type BusinessUnitDialogState =
  | { mode: 'create'; tradeId: string | null; businessUnit: null }
  | { mode: 'edit'; tradeId: string | null; businessUnit: BusinessUnit };

function TradesPage() {
  const navigate = useNavigate();
  const { data: catalog = [] } = useTradeCatalog();
  const { data: trades = [], isLoading } = useTrades('active');
  const { data: businessUnits = [] } = useBusinessUnits('all');
  const enableTrade = useEnableTrade();
  const updateTrade = useUpdateTrade();
  const createBusinessUnit = useCreateBusinessUnit();
  const updateBusinessUnit = useUpdateBusinessUnit();
  const archiveBusinessUnit = useArchiveBusinessUnit();
  const unarchiveBusinessUnit = useUnarchiveBusinessUnit();

  const [addOpen, setAddOpen] = useState(false);
  const [editTrade, setEditTrade] = useState<Trade | null>(null);
  const [removeTradeTarget, setRemoveTradeTarget] = useState<Trade | null>(null);
  const [buDialog, setBuDialog] = useState<BusinessUnitDialogState | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<BusinessUnit | null>(null);
  // What archiving the chosen unit would detach — fetched when the dialog
  // opens so the operator confirms the actual job types / members / open
  // jobs, not a bare "4 reference(s)" error after the fact.
  const archiveImpact = useBusinessUnitArchiveImpact(archiveTarget?.id ?? null);
  const archiveDetaches = (archiveImpact.data?.total ?? 0) > 0;

  const openBranding = (bu: BusinessUnit) =>
    navigate({ to: '/dashboard/settings/business-units/$id', params: { id: bu.id } });

  // Catalog trades the tenant hasn't added yet — the "Add trade" picker options.
  const available = useMemo(() => catalog.filter((c) => !c.enabled), [catalog]);
  // Slugs the catalog can restore. Removing a trade archives it; only catalog
  // trades can be re-added afterward, so we don't offer Remove for custom /
  // off-catalog trades (which would otherwise be stranded, unrecoverable here).
  const catalogSlugs = useMemo(() => new Set(catalog.map((c) => c.slug)), [catalog]);

  // Business units grouped by trade, split active + archived (archived stay
  // recoverable under their trade).
  const unitsByTrade = useMemo(() => {
    const map = new Map<string, BuGroups>();
    for (const bu of businessUnits) {
      if (!bu.tradeId) continue;
      let groups = map.get(bu.tradeId);
      if (!groups) {
        groups = { active: [], archived: [] };
        map.set(bu.tradeId, groups);
      }
      (bu.status === 'ARCHIVED' ? groups.archived : groups.active).push(bu);
    }
    return map;
  }, [businessUnits]);

  // BUs intentionally left without a trade. They remain selectable, but do not
  // create or depend on a synthetic catch-all trade.
  const noTradeUnits = useMemo<BuGroups>(() => {
    const groups: BuGroups = { active: [], archived: [] };
    for (const bu of businessUnits) {
      if (bu.tradeId) continue;
      (bu.status === 'ARCHIVED' ? groups.archived : groups.active).push(bu);
    }
    return groups;
  }, [businessUnits]);

  function unarchive(bu: BusinessUnit) {
    unarchiveBusinessUnit.mutate(bu.id, {
      onSuccess: () => toast.success(`${bu.name} restored`),
      onError: (error) =>
        toast.error(isApiError(error) ? error.message : `Could not restore ${bu.name}`),
    });
  }

  function confirmArchiveBu() {
    if (!archiveTarget || !archiveImpact.data || archiveImpact.isFetching) return;
    const target = archiveTarget;
    const impact = archiveImpact.data;
    archiveBusinessUnit.mutate(
      // Only ask the server to detach what the dialog just showed — and prove
      // it with the preview's fingerprint. A unit with nothing attached
      // archives plainly, exactly as before.
      impact.total > 0
        ? { id: target.id, detachReferences: true, expectedFingerprint: impact.fingerprint }
        : { id: target.id },
      {
        onSuccess: () => {
          toast.success(
            impact.total > 0
              ? `${target.name} archived and removed from ${describeArchiveImpact(impact)}`
              : 'Business unit archived',
          );
          setArchiveTarget(null);
        },
        onError: (error) => {
          if (!isApiError(error)) {
            toast.error('Could not archive this business unit');
            return;
          }
          toast.error(error.message);
          // Someone attached something after the preview loaded: the server
          // refused rather than detach it unseen. Reload the list so the next
          // confirm covers what is there now.
          if ((error.data as { code?: string } | undefined)?.code === ARCHIVE_IMPACT_CHANGED) {
            void archiveImpact.refetch();
          }
        },
      },
    );
  }

  function removeTrade(trade: Trade) {
    // Archive the trade (works for catalog + custom). Its business units stay
    // linked but hidden; re-adding a catalog trade restores it.
    updateTrade.mutate(
      { id: trade.id, dto: { isActive: false } },
      {
        onSuccess: () => {
          toast.success(`${trade.name} removed`);
          setRemoveTradeTarget(null);
        },
        onError: (error) =>
          toast.error(isApiError(error) ? error.message : `Could not remove ${trade.name}`),
      },
    );
  }

  function handleRemoveClick(trade: Trade) {
    const active = unitsByTrade.get(trade.id)?.active ?? [];
    if (active.length > 0) {
      setRemoveTradeTarget(trade);
      return;
    }
    removeTrade(trade);
  }

  const removeTargetUnitCount = removeTradeTarget
    ? (unitsByTrade.get(removeTradeTarget.id)?.active.length ?? 0)
    : 0;

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Trades &amp; Business Units</h1>
              <p className="text-xs text-muted-foreground">
                Manage trades and create business units with or without a trade assignment.
              </p>
            </div>
            <Button size="sm" onClick={() => setAddOpen(true)} disabled={available.length === 0}>
              <Plus className="h-4 w-4" />
              Add trade
            </Button>
          </div>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted/40" />
              ))}
            </div>
          ) : trades.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <p className="text-sm font-medium">No trades yet</p>
              <p className="max-w-sm text-xs text-muted-foreground">
                Add a trade your company offers, or create business units without assigning a trade.
              </p>
              <Button size="sm" onClick={() => setAddOpen(true)} disabled={available.length === 0}>
                <Plus className="h-4 w-4" />
                Add trade
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {trades.map((trade) => {
                const groups = unitsByTrade.get(trade.id);
                const activeCount = groups?.active.length ?? 0;
                return (
                  <section key={trade.id} className="px-4 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={cn(
                            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border',
                            tradeBadgeClass(trade.color),
                          )}
                        >
                          <TradeIcon icon={trade.icon} className="size-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{trade.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {activeCount} business unit{activeCount === 1 ? '' : 's'}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditTrade(trade)}>
                            <Pencil className="h-4 w-4" />
                            Edit color &amp; icon
                          </DropdownMenuItem>
                          {catalogSlugs.has(trade.slug) ? (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleRemoveClick(trade)}
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove trade
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <TradeUnits
                      groups={groups}
                      onAdd={() =>
                        setBuDialog({ mode: 'create', tradeId: trade.id, businessUnit: null })
                      }
                      onEdit={(bu) =>
                        setBuDialog({ mode: 'edit', tradeId: trade.id, businessUnit: bu })
                      }
                      onConfigure={openBranding}
                      onArchive={(bu) => setArchiveTarget(bu)}
                      onUnarchive={unarchive}
                      unarchivePending={unarchiveBusinessUnit.isPending}
                    />
                  </section>
                );
              })}
            </div>
          )}

          {/* Business units with no trade assignment. */}
          {!isLoading ? (
            <section className="border-t px-4 py-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                  <Building2 className="size-4" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">No trade</p>
                  <p className="truncate text-xs text-muted-foreground">
                    Business units that should not roll up under a trade.
                  </p>
                </div>
              </div>
              <TradeUnits
                groups={noTradeUnits}
                onAdd={() => setBuDialog({ mode: 'create', tradeId: null, businessUnit: null })}
                onEdit={(bu) => setBuDialog({ mode: 'edit', tradeId: null, businessUnit: bu })}
                onConfigure={openBranding}
                onArchive={(bu) => setArchiveTarget(bu)}
                onUnarchive={unarchive}
                unarchivePending={unarchiveBusinessUnit.isPending}
              />
            </section>
          ) : null}
        </div>
      </DetailPageLayout>

      {/* Add trade */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <AddTradeForm
            options={available.map((c) => ({ key: c.key, label: c.label }))}
            isPending={enableTrade.isPending}
            onClose={() => setAddOpen(false)}
            onSubmit={({ key, color, icon }) => {
              enableTrade.mutate(
                { key, color, icon },
                {
                  onSuccess: () => {
                    toast.success('Trade added');
                    setAddOpen(false);
                  },
                },
              );
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit trade color + icon */}
      <Dialog open={Boolean(editTrade)} onOpenChange={(open) => !open && setEditTrade(null)}>
        <DialogContent>
          {editTrade ? (
            <EditTradeForm
              key={editTrade.id}
              trade={editTrade}
              isPending={updateTrade.isPending}
              onClose={() => setEditTrade(null)}
              onSubmit={({ color, icon }) => {
                updateTrade.mutate(
                  { id: editTrade.id, dto: { color, icon } },
                  {
                    onSuccess: () => {
                      toast.success('Trade updated');
                      setEditTrade(null);
                    },
                  },
                );
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Remove trade confirm (only when it still has active BUs) */}
      <Dialog
        open={Boolean(removeTradeTarget)}
        onOpenChange={(open) => !open && setRemoveTradeTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove trade?</DialogTitle>
            <DialogDescription>
              {removeTradeTarget ? (
                <>
                  <strong>{removeTradeTarget.name}</strong> has {removeTargetUnitCount} business
                  unit
                  {removeTargetUnitCount === 1 ? '' : 's'} under it. Removing the trade hides it and
                  its business units; you can add the trade again later.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={updateTrade.isPending}
              onClick={() => setRemoveTradeTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={updateTrade.isPending}
              onClick={() => removeTradeTarget && removeTrade(removeTradeTarget)}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business unit add/edit */}
      <Dialog open={Boolean(buDialog)} onOpenChange={(open) => !open && setBuDialog(null)}>
        <DialogContent>
          {buDialog ? (
            <BusinessUnitForm
              key={buDialog.businessUnit?.id ?? 'create'}
              businessUnit={buDialog.businessUnit}
              isPending={createBusinessUnit.isPending || updateBusinessUnit.isPending}
              onClose={() => setBuDialog(null)}
              onSubmit={({ name, code, color, segment, serviceAreaZips }) => {
                if (buDialog.mode === 'edit') {
                  updateBusinessUnit.mutate(
                    {
                      id: buDialog.businessUnit.id,
                      dto: { name, code, color, segment, serviceAreaZips },
                    },
                    {
                      onSuccess: () => {
                        toast.success('Business unit updated');
                        setBuDialog(null);
                      },
                    },
                  );
                } else {
                  createBusinessUnit.mutate(
                    {
                      name,
                      code,
                      color,
                      segment,
                      serviceAreaZips,
                      tradeId: buDialog.tradeId ?? undefined,
                    },
                    {
                      onSuccess: () => {
                        toast.success('Business unit created');
                        setBuDialog(null);
                      },
                    },
                  );
                }
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Business unit archive confirm — previews what archiving detaches */}
      <Dialog
        open={Boolean(archiveTarget)}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive business unit?</DialogTitle>
            <DialogDescription>
              {archiveTarget ? (
                <>
                  Archive <strong>{archiveTarget.name}</strong>? It will be hidden from new job-type
                  assignment and can be restored later. Completed jobs keep reporting under it.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {archiveTarget ? (
            <ArchiveImpactPreview
              unitName={archiveTarget.name}
              impact={archiveImpact.data}
              isLoading={archiveImpact.isFetching}
              isError={archiveImpact.isError}
            />
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={archiveBusinessUnit.isPending}
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                archiveBusinessUnit.isPending || archiveImpact.isFetching || archiveImpact.isError
              }
              onClick={confirmArchiveBu}
            >
              {archiveDetaches ? 'Archive and remove from all' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function pluralize(count: number, noun: string, plural = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : plural}`;
}

/** "3 job types, 1 team member and 4 open jobs" — the success toast's summary. */
function describeArchiveImpact(impact: BusinessUnitArchiveImpact): string {
  const parts: string[] = [];
  if (impact.jobTypes.count) parts.push(pluralize(impact.jobTypes.count, 'job type'));
  if (impact.users.count) parts.push(pluralize(impact.users.count, 'team member'));
  if (impact.capacityRules.count)
    parts.push(pluralize(impact.capacityRules.count, 'capacity rule'));
  if (impact.openJobs.count) parts.push(pluralize(impact.openJobs.count, 'open job'));
  if (parts.length <= 1) return parts[0] ?? 'nothing';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * What archiving a unit will detach — the server's impact preview, so the
 * operator confirms with the actual job types / members / rules / open jobs
 * named, rather than discovering a "4 active reference(s)" refusal after
 * clicking. Completed jobs are never listed: they keep the unit for history.
 */
function ArchiveImpactPreview({
  unitName,
  impact,
  isLoading,
  isError,
}: {
  unitName: string;
  impact: BusinessUnitArchiveImpact | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }
  if (isError || !impact) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t check what {unitName} is still assigned to. Close this and try again.
      </p>
    );
  }
  if (impact.total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing is assigned to {unitName}, so it can be archived right away.
      </p>
    );
  }
  return (
    <div className="space-y-3 text-sm">
      <p>
        Archiving removes <strong>{unitName}</strong> from everything below in one step. Restoring
        the unit later will <strong>not</strong> put these back.
      </p>
      <ArchiveImpactSection
        noun="job type"
        hint="They will no longer roll up to this unit."
        section={impact.jobTypes}
        label={(r) => r.name}
      />
      <ArchiveImpactSection
        noun="team member"
        hint="Their membership in this unit is cleared."
        section={impact.users}
        label={(r) => r.name}
      />
      <ArchiveImpactSection
        noun="capacity rule"
        hint="Rules scoped only to this unit will apply to every unit."
        section={impact.capacityRules}
        label={(r) => r.name}
      />
      <ArchiveImpactSection
        noun="open job"
        hint="They will have no business unit until they are next edited."
        section={impact.openJobs}
        label={(j) => `${j.jobNumber} · ${j.name}`}
      />
    </div>
  );
}

/** One kind of reference: "3 job types — hint", the sample by name, "and N more". */
function ArchiveImpactSection<T extends { id: string }>({
  noun,
  hint,
  section,
  label,
}: {
  noun: string;
  hint: string;
  section: BusinessUnitArchiveSection<T>;
  label: (row: T) => string;
}) {
  if (section.count === 0) return null;
  const more = section.count - section.sample.length;
  return (
    <div>
      <p className="font-medium">
        {pluralize(section.count, noun)}{' '}
        <span className="font-normal text-muted-foreground">— {hint}</span>
      </p>
      <ul className="mt-1 list-disc pl-5 text-muted-foreground">
        {section.sample.map((row) => (
          <li key={row.id}>{label(row)}</li>
        ))}
        {more > 0 ? <li className="list-none -ml-5">and {more} more</li> : null}
      </ul>
    </div>
  );
}

/** Color (palette) + icon pickers shared by the Add and Edit trade forms. */
function TradeStyleFields({
  color,
  setColor,
  icon,
  setIcon,
  disabled,
}: {
  color: TradeColor;
  setColor: (c: TradeColor) => void;
  icon: string | null;
  setIcon: (i: string | null) => void;
  disabled: boolean;
}) {
  return (
    <>
      <div className="space-y-2">
        <Label>Color</Label>
        <div className="flex flex-wrap items-center gap-2">
          {TRADE_COLOR_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              disabled={disabled}
              aria-label={`Use color ${key}`}
              aria-pressed={color === key}
              onClick={() => setColor(key)}
              className={cn(
                'h-7 w-7 rounded-full ring-offset-2 ring-offset-background transition',
                TRADE_DOT_CLASS[key],
                color === key ? 'ring-2 ring-foreground' : 'ring-0',
              )}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Icon</Label>
        <div className="flex flex-wrap gap-1.5">
          {TRADE_ICON_NAMES.map((name) => (
            <button
              key={name}
              type="button"
              disabled={disabled}
              aria-label={`Use icon ${name}`}
              aria-pressed={icon === name}
              onClick={() => setIcon(icon === name ? null : name)}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-md border transition hover:bg-muted',
                icon === name ? 'border-foreground bg-muted' : 'border-border',
              )}
            >
              <TradeIcon icon={name} className="size-4" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function AddTradeForm({
  options,
  isPending,
  onClose,
  onSubmit,
}: {
  options: { key: string; label: string }[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (input: { key: string; color: TradeColor; icon: string | null }) => void;
}) {
  const [key, setKey] = useState(options[0]?.key ?? '');
  const [color, setColor] = useState<TradeColor>('blue');
  const [icon, setIcon] = useState<string | null>(null);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!key) {
      toast.error('Select a trade');
      return;
    }
    onSubmit({ key, color, icon });
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <DialogHeader>
        <DialogTitle>Add trade</DialogTitle>
        <DialogDescription>
          Choose a trade your company offers, then pick a color and icon.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1.5">
        <Label>Trade</Label>
        <Select value={key} onValueChange={setKey} disabled={isPending}>
          <SelectTrigger>
            <SelectValue placeholder="Select a trade" />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.key} value={o.key}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TradeStyleFields
        color={color}
        setColor={setColor}
        icon={icon}
        setIcon={setIcon}
        disabled={isPending}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending || !key}>
          Add trade
        </Button>
      </DialogFooter>
    </form>
  );
}

function EditTradeForm({
  trade,
  isPending,
  onClose,
  onSubmit,
}: {
  trade: Trade;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (input: { color: TradeColor; icon: string | null }) => void;
}) {
  const [color, setColor] = useState<TradeColor>(tradeColorKey(trade.color));
  const [icon, setIcon] = useState<string | null>(trade.icon ?? null);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ color, icon });
      }}
      className="space-y-5"
    >
      <DialogHeader>
        <DialogTitle>Edit {trade.name}</DialogTitle>
        <DialogDescription>Change this trade&apos;s color and icon.</DialogDescription>
      </DialogHeader>

      <TradeStyleFields
        color={color}
        setColor={setColor}
        icon={icon}
        setIcon={setIcon}
        disabled={isPending}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          Save
        </Button>
      </DialogFooter>
    </form>
  );
}

/** A trade's business units — active list (edit/archive) + a recoverable
 * archived list (restore) + an add button. */
function TradeUnits({
  groups,
  onAdd,
  onEdit,
  onConfigure,
  onArchive,
  onUnarchive,
  unarchivePending,
}: {
  groups: BuGroups | undefined;
  onAdd: () => void;
  onEdit: (bu: BusinessUnit) => void;
  onConfigure: (bu: BusinessUnit) => void;
  onArchive: (bu: BusinessUnit) => void;
  onUnarchive: (bu: BusinessUnit) => void;
  unarchivePending: boolean;
}) {
  const active = groups?.active ?? [];
  const archived = groups?.archived ?? [];
  // Tax Policy layer (phase 4): active tax rules per unit, as a row chip.
  const { data: taxPolicies } = useTaxPolicies();
  const taxRuleCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const policy of taxPolicies ?? []) {
      if (!policy.businessUnitId) continue;
      counts.set(policy.businessUnitId, (counts.get(policy.businessUnitId) ?? 0) + 1);
    }
    return counts;
  }, [taxPolicies]);

  return (
    <div className="mt-3 space-y-2 rounded-lg border bg-muted/20 p-3">
      {active.length === 0 && archived.length === 0 ? (
        <p className="px-1 py-2 text-xs text-muted-foreground">No business units yet.</p>
      ) : null}

      {active.length > 0 ? (
        <ul className="space-y-1.5">
          {active.map((bu) => (
            <li
              key={bu.id}
              className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full border"
                  style={{ backgroundColor: bu.color }}
                />
                <span className="truncate text-sm font-medium">{bu.name}</span>
                {bu.code ? (
                  <Badge variant="outline" className="font-mono text-xs">
                    {bu.code}
                  </Badge>
                ) : null}
                <BusinessUnitAxes businessUnit={bu} taxRuleCount={taxRuleCounts.get(bu.id) ?? 0} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(bu)}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onConfigure(bu)}>
                    <Palette className="h-4 w-4" />
                    Branding &amp; templates
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => onArchive(bu)}
                  >
                    <Archive className="h-4 w-4" />
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      ) : null}

      {archived.length > 0 ? (
        <div className="space-y-1.5">
          <p className="px-1 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Archived
          </p>
          <ul className="space-y-1.5">
            {archived.map((bu) => (
              <li
                key={bu.id}
                className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 opacity-70"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full border"
                    style={{ backgroundColor: bu.color }}
                  />
                  <span className="truncate text-sm font-medium line-through">{bu.name}</span>
                  {bu.code ? (
                    <Badge variant="outline" className="font-mono text-xs">
                      {bu.code}
                    </Badge>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={unarchivePending}
                  onClick={() => onUnarchive(bu)}
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4" />
        Add business unit
      </Button>
    </div>
  );
}

/** The resolution axes a unit carries, as small chips on its row. */
function BusinessUnitAxes({
  businessUnit,
  taxRuleCount = 0,
}: {
  businessUnit: BusinessUnit;
  /** Tax Policy layer (phase 4): active tax rules naming this unit. */
  taxRuleCount?: number;
}) {
  const chips: string[] = [BUSINESS_UNIT_SEGMENT_LABELS[businessUnit.segment]];
  const zipCount = businessUnit.serviceAreaZips?.length ?? 0;
  if (zipCount > 0) {
    chips.push(`${zipCount} ZIP${zipCount === 1 ? '' : 's'}`);
  }
  if (chips.length === 0 && taxRuleCount === 0) return null;
  return (
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-left">
      {chips.map((chip) => (
        <Badge key={chip} variant="secondary" className="text-xs font-normal">
          {chip}
        </Badge>
      ))}
      {taxRuleCount > 0 ? (
        <Link
          to="/dashboard/taxes/rules"
          title="This unit has its own tax rules (Settings → Taxes → Rules)"
          onClick={(e) => e.stopPropagation()}
        >
          <Badge
            variant="outline"
            className="border-transparent bg-indigo-100 text-xs font-normal text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
          >
            {taxRuleCount} tax {taxRuleCount === 1 ? 'rule' : 'rules'}
          </Badge>
        </Link>
      ) : null}
    </span>
  );
}

function BusinessUnitForm({
  businessUnit,
  isPending,
  onClose,
  onSubmit,
}: {
  businessUnit: BusinessUnit | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    code: string;
    color: string;
    segment: BusinessUnitSegment;
    serviceAreaZips: string[];
  }) => void;
}) {
  const isEdit = Boolean(businessUnit);
  const [name, setName] = useState(businessUnit?.name ?? '');
  const [code, setCode] = useState(businessUnit?.code ?? '');
  // The code auto-derives from the name until the operator edits it directly.
  const [codeTouched, setCodeTouched] = useState(isEdit);
  const [color, setColor] = useState(businessUnit?.color ?? BU_DEFAULT_COLOR);
  // No default on purpose: residential vs commercial is the split that decides
  // which unit a job lands on, so a new unit must state it.
  const [segment, setSegment] = useState<BusinessUnitSegment | null>(businessUnit?.segment ?? null);
  const [serviceAreaZips, setServiceAreaZips] = useState<string[]>(
    businessUnit?.serviceAreaZips ?? [],
  );
  const { data: serviceAreas = [] } = useServiceAreas();

  const effectiveCode = codeTouched ? code : deriveBuCode(name);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    if (!segment) {
      toast.error('Choose which customers this unit serves');
      return;
    }
    onSubmit({ name: trimmedName, code: effectiveCode.trim(), color, segment, serviceAreaZips });
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit business unit' : 'Add business unit'}</DialogTitle>
      </DialogHeader>

      <div className="space-y-1.5">
        <Label htmlFor="bu-name">Name</Label>
        <Input
          id="bu-name"
          value={name}
          disabled={isPending}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. HVAC — North"
          autoFocus
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="bu-code">Code</Label>
        <Input
          id="bu-code"
          value={effectiveCode}
          disabled={isPending}
          onChange={(e) => {
            setCodeTouched(true);
            setCode(e.target.value);
          }}
          placeholder="HVAC-N"
        />
        <p className="text-xs text-muted-foreground">
          Auto-generated from the name — edit if you want a different one.
        </p>
      </div>

      <div className="space-y-2">
        <Label>
          Customers <span className="text-destructive">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Customers">
          {(Object.keys(BUSINESS_UNIT_SEGMENT_LABELS) as BusinessUnitSegment[]).map((key) => {
            const selected = segment === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={isPending}
                onClick={() => setSegment(key)}
                className={cn(
                  'rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  selected
                    ? 'border-foreground bg-muted font-medium'
                    : 'border-border hover:bg-muted/50',
                )}
              >
                <div>{BUSINESS_UNIT_SEGMENT_LABELS[key]}</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {BUSINESS_UNIT_SEGMENT_HINTS[key]}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          When a job type is listed under units that serve different customers (for example
          Residential + Commercial, or Both + Commercial), Iris asks whether the address is a home
          or a business and the unit is picked by the answer.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>Service areas</Label>
        <ServiceAreaZipPicker
          subject="this unit"
          serviceAreas={serviceAreas}
          selectedZips={serviceAreaZips}
          onChange={setServiceAreaZips}
        />
        <p className="text-xs text-muted-foreground">
          The service address&apos;s area picks this unit (North vs South). Leave empty for every
          location.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Color</Label>
        <div className="flex flex-wrap items-center gap-2">
          {BU_PRESET_COLORS.map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={isPending}
              aria-label={`Use color ${preset}`}
              aria-pressed={color === preset}
              onClick={() => setColor(preset)}
              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
              style={{
                backgroundColor: preset,
                borderColor: color === preset ? 'hsl(var(--foreground))' : 'transparent',
              }}
            />
          ))}
          <input
            type="color"
            value={color}
            disabled={isPending}
            onChange={(e) => setColor(e.target.value)}
            className="h-7 w-7 cursor-pointer rounded-full border-0 p-0"
            aria-label="Custom color"
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isPending || !name.trim() || !effectiveCode.trim() || !segment}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}
